import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import InputEditor from './InputEditor';
import ResultSummary from './ResultSummary';
import { GeneratorMark } from './PedigreeGlyph';
import { generatePedigree } from './generatePedigree';
import { CHROME, PANEL_WIDTH, PANEL_WIDTH_REVIEW } from './tokens';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const PLACEHOLDER = `Describe the family, or paste a pedigree in any format.

e.g. Proband's mother had breast cancer at 46.
     Two maternal aunts, one affected.`;

// Enough to be a description rather than a stray keystroke.
const MIN_CHARS = 3;

const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

// input → generating → ready, and back to input with a message on failure.
export default function GeneratorPanel({ onClose, onApply, accent }) {
  const t = useAutoTranslation();

  const [text, setText] = React.useState('');
  const [status, setStatus] = React.useState('input');
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [applying, setApplying] = React.useState(false);
  // Set when the backend refuses because the family already has members.
  const [needsReplace, setNeedsReplace] = React.useState(false);
  // term -> { id, name } | { other: true }
  const [choices, setChoices] = React.useState({});
  const abortRef = React.useRef(null);

  const busy = status === 'generating';
  const canGenerate = text.trim().length >= MIN_CHARS && !busy;
  const reviewing = status === 'ready' && result;

  // So a late response cannot set state on a closed panel.
  React.useEffect(() => () => abortRef.current?.abort(), []);

  const handleChange = (next) => {
    setText(next);
    if (error) setError(null);
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus('generating');
    setError(null);
    try {
      const draft = await generatePedigree(
        { text },
        { signal: controller.signal }
      );
      setResult(draft);
      setChoices({});
      setStatus('ready');
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      setError(
        err?.message || t('Could not generate the pedigree. Try again.')
      );
      setStatus('input');
    }
  };

  const handleChoose = (term, choice) =>
    setChoices((current) => {
      const next = { ...current };
      if (choice) next[term] = choice;
      else delete next[term];
      return next;
    });

  // The error shows in place rather than the panel closing on a write that did
  // not happen.
  const handleApply = async () => {
    setApplying(true);
    setError(null);
    try {
      await onApply?.(result, { replace: needsReplace, choices });
      setNeedsReplace(false);
    } catch (err) {
      setError(err?.message || t('Could not apply the pedigree.'));
      // Stay on the draft: clearing the existing family is the user's call.
      if (err?.replaceRequired) setNeedsReplace(true);
      else setStatus('input');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box
      role="dialog"
      aria-label={t('Pedigree sketch')}
      sx={{
        width: reviewing ? PANEL_WIDTH_REVIEW : PANEL_WIDTH,
        maxWidth: 'calc(100vw - 32px)',
        transition: 'width 180ms cubic-bezier(0.2, 0, 0, 1)',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        backgroundColor: CHROME.surface,
        border: `1px solid ${CHROME.border}`,
        borderRadius: 2,
        boxShadow: '0 8px 28px rgba(15, 30, 38, 0.18)',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.75,
          py: 1.25,
          backgroundColor: CHROME.header,
          borderBottom: `1px solid ${CHROME.border}`
        }}
      >
        <GeneratorMark size={22} color={accent} />
        <Typography
          sx={{
            flex: 1,
            fontSize: '0.9rem',
            fontWeight: 700,
            color: CHROME.text
          }}
        >
          {t('Pedigree sketch')}
        </Typography>
        <Tooltip title={t('Close')}>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t('Close sketch')}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          p: 1.75,
          // Capped against the viewport: on a short window the apply button
          // would otherwise sit below the bottom of the screen.
          maxHeight: 'calc(100vh - 160px)',
          overflowY: 'auto'
        }}
      >
        {reviewing ? (
          <ResultSummary
            error={error}
            result={result}
            accent={accent}
            onEdit={() => setStatus('input')}
            onApply={handleApply}
            applying={applying}
            needsReplace={needsReplace}
            choices={choices}
            onChoose={handleChoose}
          />
        ) : (
          <>
            <InputEditor
              value={text}
              onChange={handleChange}
              onSubmit={handleGenerate}
              disabled={busy}
              autoFocus
              placeholder={t(PLACEHOLDER)}
              accent={accent}
            />

            {error && (
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: '0.72rem',
                  color: 'error.main',
                  lineHeight: 1.45
                }}
              >
                {error}
              </Typography>
            )}

            <Box
              sx={{
                mt: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}
            >
              <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={handleGenerate}
                disabled={!canGenerate}
                startIcon={
                  busy ? (
                    <CircularProgress size={13} sx={{ color: 'inherit' }} />
                  ) : null
                }
                sx={{
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  backgroundColor: accent,
                  '&:hover': {
                    backgroundColor: accent,
                    filter: 'brightness(0.93)'
                  }
                }}
              >
                {busy ? t('Reading family…') : t('Sketch')}
              </Button>
            </Box>

            <Typography
              sx={{
                mt: 1,
                fontSize: '0.68rem',
                color: CHROME.muted,
                lineHeight: 1.5
              }}
            >
              {t('Nothing is drawn until you review the pedigree.')}{' '}
              {IS_MAC ? '⌘' : t('Ctrl')}
              {t('+Enter sketches.')}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
